import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import classNames from 'classnames';

import { fromNow } from '@sv-support-sdk/utils/src/helpers/toLocaleTime';
import { sanitizeRegular } from '@sv-support-sdk/utils/src/constants/sanitizeConfigs';
import Paragraph from '@sv-support-sdk/ui-components/src/components/Typography/Paragraph';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Breadcrumbs, { IBreadcrumb } from '../../components/Navigation/Breadcrumbs';
import ArticleBlock from '../../components/Layout/ArticleBlock';
import ArticleBlockMain from '../../components/Layout/ArticleBlock/ArticleBlockMain';
import LoaderFallback from '../../components/Feedback/Loader/LoaderFallback';
import Article from '../../components/Content/Article';
import ArticleBlockAside from '../../components/Layout/ArticleBlock/ArticleBlockAside';
import CustomerEffortBlock from '../../components/Pages/HelpCenterTopic/CustomerEffortBlock';
import Document from '../../components/Content/Document';
import Tabs from '../../components/Navigation/NavTabs';

import { createBreadcrumbs } from '../../helpers/breadcrumbs';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import modalActions from '../../store/actions/modals';
import {
  categoryBySlugSelector,
  relatedTopicsSelector,
  selectCategories,
  selectLoading,
  topicBySlugSelector,
} from '../../store/selectors/helpCenter';
import helpCenterActions from '../../store/actions/helpCenter';
import { $l } from '../../locales';
import { FULL_SIZE_IMAGE_MODAL } from '../../constants/modals';
import useMobileDetect from '../../hooks/useMobileDetect';

const HelpCenterTopic = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topic = useAppSelector((state) =>
    topicBySlugSelector(state, params.category, params.topic),
  );
  const category = useAppSelector((state) => categoryBySlugSelector(state, params.category));
  const relatedTopics = useAppSelector((state) =>
    relatedTopicsSelector(state, params.category, params.topic),
  );
  const categories = useAppSelector(selectCategories);
  const loading = useAppSelector(selectLoading);
  const isMobile = useMobileDetect();

  useEffect(() => {
    dispatch(helpCenterActions.getCategories());
    dispatch(
      helpCenterActions.getTopics({
        noteTitle: $l('HELP_CENTER_TOPIC.NOTE'),
        tipTitle: $l('HELP_CENTER_TOPIC.TIP'),
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!loading.categories && !loading.topics && topic && topic.description) {
      const hash = location.hash;
      if (hash) {
        const id = hash.slice(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
        }
      }
    }
  }, [location.hash, loading, topic]);

  if (loading.categories || loading.topics) {
    return (
      <PageContentInner height="full">
        <LoaderFallback />
      </PageContentInner>
    );
  }

  if (!topic || !category) {
    return null;
  }

  const breadcrumbs: IBreadcrumb[] = [
    ...createBreadcrumbs({ categories, category }),
    { text: topic.name, isLast: true },
  ];

  const openImageInFullSize = (event: MouseEvent) => {
    const { tagName, currentSrc } = event.target as HTMLImageElement;

    if (tagName !== 'IMG') return;

    dispatch(
      modalActions.openModal({ name: FULL_SIZE_IMAGE_MODAL, props: { imageLink: currentSrc } }),
    );
  };

  return (
    <PageContentInner>
      <Breadcrumbs items={breadcrumbs} className="indent-md-bottom" />
      <Headline className="indent-sm-bottom">{topic.name}</Headline>
      <Paragraph className={classNames({
        [isMobile ? "indent-lg-bottom" : "indent-xxl-bottom"]: true
      })}>
        {`${$l('HELP_CENTER_TOPIC.UPDATED')} ${fromNow(topic.updatedAt)}`}
      </Paragraph>
      <ArticleBlock>
        <ArticleBlockMain>
          {topic.documents &&
            topic.documents.map((document) => <Document key={document.id} document={document} />)}
          <Article onClick={openImageInFullSize}>
            <div
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(topic.description, sanitizeRegular) }}
            ></div>
          </Article>
          <CustomerEffortBlock slug={topic.slug} />
        </ArticleBlockMain>
        <ArticleBlockAside>
          {/* @ts-ignore */}
          {relatedTopics && relatedTopics.length > 0 && (
            <>
              <Headline type="secondary" className={classNames('indent-sm-bottom', {
                "indent-md-left": !isMobile
              })}>
                {$l('HELP_CENTER_TOPIC.RELATED_ARTICLES')}
              </Headline>
              <Tabs tabs={relatedTopics} />
            </>
          )}
        </ArticleBlockAside>
      </ArticleBlock>
    </PageContentInner>
  );
};

export default HelpCenterTopic;
